import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Card, Tabs, message, Tooltip, Statistic, Row, Col, Select, InputNumber } from 'antd';
import type { TabsProps } from 'antd';
import {
  ArrowLeftOutlined,
  ClockCircleOutlined,
  KeyOutlined,
  GlobalOutlined,
  DeleteOutlined,
  FileTextOutlined,
  CopyOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import { copyToClipboard } from '../utils/clipboard';
import './Toolbox.css';

const { TextArea } = Input;

// ===================== 时间戳工具 =====================
const TimestampTool: React.FC = () => {
  const [now, setNow] = useState(Math.floor(Date.now() / 1000));
  const [inputTimestamp, setInputTimestamp] = useState('');
  const [inputDate, setInputDate] = useState('');
  const [timestampResult, setTimestampResult] = useState('');
  const [dateResult, setDateResult] = useState('');

  useEffect(() => {
    const timer = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(timer);
  }, []);

  const handleTimestampToDate = () => {
    const ts = Number(inputTimestamp.trim());
    if (!ts || isNaN(ts)) {
      message.warning('请输入有效的时间戳');
      return;
    }
    // 支持秒和毫秒
    const ms = ts > 1e12 ? ts : ts * 1000;
    const d = new Date(ms);
    if (isNaN(d.getTime())) {
      message.error('时间戳无效');
      return;
    }
    setTimestampResult(d.toLocaleString('zh-CN', { hour12: false }));
  };

  const handleDateToTimestamp = () => {
    const d = new Date(inputDate.trim());
    if (isNaN(d.getTime())) {
      message.warning('请输入有效的日期时间');
      return;
    }
    setDateResult(String(Math.floor(d.getTime() / 1000)));
  };

  return (
    <div className="toolbox-tool-content">
      <div className="toolbox-current-ts">
        <Statistic title="当前时间戳（秒）" value={now} />
        <Button size="small" icon={<CopyOutlined />} onClick={() => { copyToClipboard(String(now)).then((ok) => ok && message.success('已复制')); }}>复制</Button>
      </div>

      <Card title="时间戳 → 日期" size="small" className="toolbox-card">
        <Input
          placeholder="输入时间戳（秒或毫秒）"
          value={inputTimestamp}
          onChange={(e) => setInputTimestamp(e.target.value)}
          onPressEnter={handleTimestampToDate}
          suffix={<Button type="link" size="small" onClick={() => setInputTimestamp(String(now))}>用当前</Button>}
        />
        <Button type="primary" icon={<ThunderboltOutlined />} onClick={handleTimestampToDate} style={{ marginTop: 8 }}>转换</Button>
        {timestampResult && (
          <div className="toolbox-result">
            <span>结果: {timestampResult}</span>
            <Button size="small" type="link" icon={<CopyOutlined />} onClick={() => { copyToClipboard(timestampResult); message.success('已复制'); }}>复制</Button>
          </div>
        )}
      </Card>

      <Card title="日期 → 时间戳" size="small" className="toolbox-card">
        <Input
          placeholder="输入日期时间，如 2026-06-20 12:00:00"
          value={inputDate}
          onChange={(e) => setInputDate(e.target.value)}
          onPressEnter={handleDateToTimestamp}
        />
        <Button type="primary" icon={<ThunderboltOutlined />} onClick={handleDateToTimestamp} style={{ marginTop: 8 }}>转换</Button>
        {dateResult && (
          <div className="toolbox-result">
            <span>结果: {dateResult}</span>
            <Button size="small" type="link" icon={<CopyOutlined />} onClick={() => { copyToClipboard(dateResult); message.success('已复制'); }}>复制</Button>
          </div>
        )}
      </Card>
    </div>
  );
};

// ===================== UUID 生成工具 =====================
const UUIDTool: React.FC = () => {
  const [uuids, setUuids] = useState<string[]>([]);
  const [count, setCount] = useState(1);
  const [uppercase, setUppercase] = useState(false);
  const [noHyphens, setNoHyphens] = useState(false);

  const generateUUID = (): string => {
    // RFC4122 v4
    const hex = '0123456789abcdef';
    let uuid = '';
    for (let i = 0; i < 36; i++) {
      if (i === 8 || i === 13 || i === 18 || i === 23) {
        uuid += '-';
      } else if (i === 14) {
        uuid += '4';
      } else if (i === 19) {
        uuid += hex[(Math.random() * 4) | 8];
      } else {
        uuid += hex[(Math.random() * 16) | 0];
      }
    }
    if (noHyphens) {
      uuid = uuid.replace(/-/g, '');
    }
    return uppercase ? uuid.toUpperCase() : uuid;
  };

  const handleGenerate = () => {
    const n = Math.min(Math.max(count, 1), 100);
    const list: string[] = [];
    for (let i = 0; i < n; i++) {
      list.push(generateUUID());
    }
    setUuids(list);
  };

  useEffect(() => {
    handleGenerate();
  }, []);

  return (
    <div className="toolbox-tool-content">
      <div className="toolbox-controls">
        <span>生成数量:</span>
        <InputNumber
          min={1}
          max={100}
          value={count}
          onChange={(v) => setCount(v || 1)}
          style={{ width: 80 }}
        />
        <label className="toolbox-checkbox">
          <input type="checkbox" checked={uppercase} onChange={(e) => setUppercase(e.target.checked)} />
          大写
        </label>
        <label className="toolbox-checkbox">
          <input type="checkbox" checked={noHyphens} onChange={(e) => setNoHyphens(e.target.checked)} />
          去除连接符
        </label>
        <Button type="primary" icon={<ReloadOutlined />} onClick={handleGenerate}>生成</Button>
        {uuids.length > 0 && (
          <Button icon={<CopyOutlined />} onClick={() => { copyToClipboard(uuids.join('\n')); message.success('已复制全部'); }}>复制全部</Button>
        )}
      </div>
      <div className="toolbox-uuid-list">
        {uuids.map((uuid, i) => (
          <div key={i} className="toolbox-uuid-item">
            <code>{uuid}</code>
            <Button size="small" type="link" icon={<CopyOutlined />} onClick={() => { copyToClipboard(uuid); message.success('已复制'); }} />
          </div>
        ))}
      </div>
    </div>
  );
};

// ===================== 世界时钟 =====================
const WorldClockTool: React.FC = () => {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const cities = [
    { city: '北京', tz: 'Asia/Shanghai', flag: '🇨🇳' },
    { city: '东京', tz: 'Asia/Tokyo', flag: '🇯🇵' },
    { city: '伦敦', tz: 'Europe/London', flag: '🇬🇧' },
    { city: '纽约', tz: 'America/New_York', flag: '🇺🇸' },
    { city: '洛杉矶', tz: 'America/Los_Angeles', flag: '🇺🇸' },
    { city: '巴黎', tz: 'Europe/Paris', flag: '🇫🇷' },
    { city: '悉尼', tz: 'Australia/Sydney', flag: '🇦🇺' },
    { city: '迪拜', tz: 'Asia/Dubai', flag: '🇦🇪' },
    { city: '新加坡', tz: 'Asia/Singapore', flag: '🇸🇬' },
    { city: '莫斯科', tz: 'Europe/Moscow', flag: '🇷🇺' },
  ];

  const formatTime = (tz: string) => {
    try {
      return now.toLocaleString('zh-CN', {
        timeZone: tz,
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return '不支持';
    }
  };

  return (
    <div className="toolbox-tool-content">
      <Row gutter={[12, 12]}>
        {cities.map((c) => (
          <Col xs={12} sm={8} md={6} key={c.city}>
            <Card size="small" className="toolbox-clock-card">
              <div className="toolbox-clock-flag">{c.flag}</div>
              <div className="toolbox-clock-city">{c.city}</div>
              <div className="toolbox-clock-time">{formatTime(c.tz)}</div>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};

// ===================== 文本去重 =====================
const TextDedupTool: React.FC = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [mode, setMode] = useState<'line' | 'all'>('line');
  const [ignoreCase, setIgnoreCase] = useState(false);
  const [stats, setStats] = useState({ before: 0, after: 0, removed: 0 });

  const handleDedup = () => {
    if (!input.trim()) {
      message.warning('请输入文本');
      return;
    }
    let lines: string[];
    if (mode === 'line') {
      lines = input.split('\n');
    } else {
      lines = input.split(/[\s,，;；、。.\n]+/).filter(Boolean);
    }
    const seen = new Set<string>();
    const result: string[] = [];
    for (const line of lines) {
      const key = ignoreCase ? line.toLowerCase() : line;
      if (!seen.has(key)) {
        seen.add(key);
        result.push(line);
      }
    }
    const out = mode === 'line' ? result.join('\n') : result.join('\n');
    setOutput(out);
    setStats({ before: lines.length, after: result.length, removed: lines.length - result.length });
  };

  return (
    <div className="toolbox-tool-content">
      <div className="toolbox-controls">
        <Select
          value={mode}
          onChange={setMode}
          style={{ width: 120 }}
          options={[
            { label: '按行去重', value: 'line' },
            { label: '按词去重', value: 'all' },
          ]}
        />
        <label className="toolbox-checkbox">
          <input type="checkbox" checked={ignoreCase} onChange={(e) => setIgnoreCase(e.target.checked)} />
          忽略大小写
        </label>
        <Button type="primary" icon={<ThunderboltOutlined />} onClick={handleDedup}>去重</Button>
        <Button onClick={() => { setInput(''); setOutput(''); setStats({ before: 0, after: 0, removed: 0 }); }}>清空</Button>
      </div>
      {stats.before > 0 && (
        <div className="toolbox-stats">
          <span>原始: {stats.before}</span>
          <span>去重后: {stats.after}</span>
          <span>已删除: {stats.removed}</span>
        </div>
      )}
      <div className="toolbox-text-grid toolbox-text-grid-fill">
        <div className="toolbox-text-col">
          <div className="toolbox-text-label">输入文本</div>
          <TextArea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            rows={10}
            placeholder="输入需要去重的文本..."
            className="toolbox-textarea-fill"
          />
        </div>
        <div className="toolbox-text-col">
          <div className="toolbox-text-label">
            结果
            {output && <Button size="small" type="link" icon={<CopyOutlined />} onClick={() => { copyToClipboard(output); message.success('已复制'); }}>复制</Button>}
          </div>
          <TextArea
            value={output}
            rows={10}
            readOnly
            placeholder="去重结果..."
            className="toolbox-textarea-fill"
          />
        </div>
      </div>
    </div>
  );
};

// ===================== 字数统计 =====================
const WordCountTool: React.FC = () => {
  const [input, setInput] = useState('');
  const [stats, setStats] = useState({
    chars: 0,
    charsNoSpace: 0,
    words: 0,
    lines: 0,
    paragraphs: 0,
    chineseChars: 0,
    englishWords: 0,
    digits: 0,
  });

  const handleCount = useCallback((text: string) => {
    const chars = text.length;
    const charsNoSpace = text.replace(/\s/g, '').length;
    const lines = text === '' ? 0 : text.split('\n').length;
    const paragraphs = text === '' ? 0 : text.split(/\n\s*\n/).filter((s) => s.trim()).length;
    const chineseChars = (text.match(/[\u4e00-\u9fa5]/g) || []).length;
    const englishWords = (text.match(/[a-zA-Z]+/g) || []).length;
    const digits = (text.match(/\d/g) || []).length;
    const words = chineseChars + englishWords;
    setStats({ chars, charsNoSpace, words, lines, paragraphs, chineseChars, englishWords, digits });
  }, []);

  useEffect(() => {
    handleCount(input);
  }, [input, handleCount]);

  return (
    <div className="toolbox-tool-content toolbox-wordcount">
      <TextArea
        value={input}
        onChange={(e) => setInput(e.target.value)}
        rows={10}
        placeholder="输入或粘贴文本进行统计..."
        className="toolbox-textarea-fill"
      />
      <Row gutter={[12, 12]} style={{ marginTop: 16, flexShrink: 0 }}>
        <Col xs={12} sm={8} md={6}>
          <Card size="small"><Statistic title="总字符数" value={stats.chars} /></Card>
        </Col>
        <Col xs={12} sm={8} md={6}>
          <Card size="small"><Statistic title="不含空格" value={stats.charsNoSpace} /></Card>
        </Col>
        <Col xs={12} sm={8} md={6}>
          <Card size="small"><Statistic title="总词数" value={stats.words} /></Card>
        </Col>
        <Col xs={12} sm={8} md={6}>
          <Card size="small"><Statistic title="行数" value={stats.lines} /></Card>
        </Col>
        <Col xs={12} sm={8} md={6}>
          <Card size="small"><Statistic title="段落数" value={stats.paragraphs} /></Card>
        </Col>
        <Col xs={12} sm={8} md={6}>
          <Card size="small"><Statistic title="中文字符" value={stats.chineseChars} /></Card>
        </Col>
        <Col xs={12} sm={8} md={6}>
          <Card size="small"><Statistic title="英文单词" value={stats.englishWords} /></Card>
        </Col>
        <Col xs={12} sm={8} md={6}>
          <Card size="small"><Statistic title="数字字符" value={stats.digits} /></Card>
        </Col>
      </Row>
    </div>
  );
};

// ===================== 主页面 =====================
const Toolbox: React.FC = () => {
  const navigate = useNavigate();

  const items: TabsProps['items'] = [
    {
      key: 'timestamp',
      label: (<span><ClockCircleOutlined /> 时间戳转换</span>),
      children: <TimestampTool />,
    },
    {
      key: 'uuid',
      label: (<span><KeyOutlined /> UUID 生成</span>),
      children: <UUIDTool />,
    },
    {
      key: 'worldclock',
      label: (<span><GlobalOutlined /> 世界时钟</span>),
      children: <WorldClockTool />,
    },
    {
      key: 'dedup',
      label: (<span><DeleteOutlined /> 文本去重</span>),
      children: <TextDedupTool />,
    },
    {
      key: 'wordcount',
      label: (<span><FileTextOutlined /> 字数统计</span>),
      children: <WordCountTool />,
    },
  ];

  return (
    <div className="toolbox-page">
      <div className="toolbox-container">
        <div className="toolbox-header">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/')}
            className="toolbox-back"
          >
            返回首页
          </Button>
          <h1 className="toolbox-title">
            <ToolOutlined /> Tomato 工具箱
          </h1>
          <div style={{ width: 100 }} />
        </div>
        <div className="toolbox-body">
          <Tabs defaultActiveKey="timestamp" size="large" items={items} destroyInactiveTabPane />
        </div>
      </div>
    </div>
  );
};

export default Toolbox;
